import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { HazardCategory, HazardSeverity, HazardLikelihood, FALLBACK_HAZARD_CATEGORIES } from '../types';
import { hazardService } from '../hazardService';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';
import { administrationService, Department } from '../../../administration/administrationService';

interface CreateHazardModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export const CreateHazardModal: React.FC<CreateHazardModalProps> = ({ onClose, onCreated }) => {
  const [categories, setCategories] = useState<HazardCategory[]>(FALLBACK_HAZARD_CATEGORIES);
  const [categoryId, setCategoryId] = useState('');
  const [departmentsList, setDepartmentsList] = useState<Department[]>([]);
  const [title, setTitle] = useState('');
  const [conditionText, setConditionText] = useState('');
  const [consequenceText, setConsequenceText] = useState('');
  const [potentialOutcome, setPotentialOutcome] = useState('');
  const [severityEstimate, setSeverityEstimate] = useState<HazardSeverity>('MEDIUM');
  const [likelihoodEstimate, setLikelihoodEstimate] = useState<HazardLikelihood>('POSSIBLE');
  const [initialRiskIndex, setInitialRiskIndex] = useState('3C');
  const [department, setDepartment] = useState('');
  const [location, setLocation] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [reviewFrequencyDays, setReviewFrequencyDays] = useState<number>(90);
  const [loading, setLoading] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    hazardService.getCategories().then((cats) => {
      const merged: HazardCategory[] = [];
      const seenCodes = new Set<string>();

      // Put API categories first
      for (const c of (cats || [])) {
        const key = (c.code || '').trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(c);
        }
      }

      // Add fallbacks if not present
      for (const f of FALLBACK_HAZARD_CATEGORIES) {
        const key = f.code.trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(f);
        }
      }

      setCategories(merged);
      if (merged.length > 0 && !categoryId) {
        setCategoryId(merged[0].id);
      }
    }).catch(() => {
      setCategories(FALLBACK_CATEGORIES);
      if (!categoryId) {
        setCategoryId(FALLBACK_CATEGORIES[0].id);
      }
    });

    administrationService.getDepartments().then((depts) => {
      const activeDepts = depts.filter((d) => d.status === 'ACTIVE');
      setDepartmentsList(activeDepts);
      if (activeDepts.length > 0) {
        setDepartment(activeDepts[0].name);
      }
    }).catch(() => {});
  }, []);

  const updateRiskIndex = (sev: HazardSeverity, lik: HazardLikelihood) => {
    const sevMap: Record<HazardSeverity, number> = { LOW: 2, MEDIUM: 3, HIGH: 4, CRITICAL: 5 };
    const likMap: Record<HazardLikelihood, string> = { RARE: 'E', UNLIKELY: 'D', POSSIBLE: 'C', PROBABLE: 'B', FREQUENT: 'A' };
    const code = `${sevMap[sev]}${likMap[lik]}`;
    setInitialRiskIndex(code);
  };

  const handleSeverityChange = (s: HazardSeverity) => {
    setSeverityEstimate(s);
    updateRiskIndex(s, likelihoodEstimate);
  };

  const handleLikelihoodChange = (l: HazardLikelihood) => {
    setLikelihoodEstimate(l);
    updateRiskIndex(severityEstimate, l);
  };

  // Validation Flags
  const titleError = hasAttemptedSubmit && !title.trim();
  const categoryError = hasAttemptedSubmit && !categoryId.trim();
  const conditionError = hasAttemptedSubmit && !conditionText.trim();
  const consequenceError = hasAttemptedSubmit && !consequenceText.trim();
  const hasErrors = titleError || categoryError || conditionError || consequenceError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!title.trim() || !conditionText.trim() || !consequenceText.trim() || !categoryId.trim()) {
      return;
    }

    try {
      setLoading(true);
      await hazardService.createHazard({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        title: title.trim(),
        categoryId: categoryId.trim(),
        conditionText: conditionText.trim(),
        consequenceText: consequenceText.trim(),
        potentialOutcome: potentialOutcome.trim() || undefined,
        severityEstimate,
        likelihoodEstimate,
        initialRiskIndex,
        department: department.trim() || undefined,
        location: location.trim() || undefined,
        ownerName: ownerName.trim() || undefined,
        ownerId: ownerName.trim() ? `USR-${Date.now().toString().slice(-4)}` : undefined,
        reviewFrequencyDays,
        createdBy: 'SAFETY_OFFICER',
      });
      onCreated();
    } catch (err: any) {
      alert(`Tehlike kaydedilemedi: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div
        className="modal-card modal-lg"
        style={{
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0, borderBottom: '1px solid #e2e8f0', padding: '16px 24px' }}>
          <div className="modal-header-content">
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
              Identify New Safety Hazard
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              Standard Condition → Consequence → Potential Outcome Identification
            </p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
            {/* Validation Alert Banner */}
            {hasAttemptedSubmit && hasErrors && (
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #f87171',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <AlertTriangle size={20} color="#dc2626" style={{ flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: '#991b1b', fontWeight: 600 }}>
                  Lütfen kırmızı çerçeve ile belirtilen zorunlu alanları (Başlık, Taksonomi Kategorisi, Koşul ve Sonuç) doldurunuz.
                </div>
              </div>
            )}

            {/* Title & Category */}
            <div className="form-grid-2" style={{ gap: '16px', marginBottom: '16px' }}>
              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Hazard Title <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Unstable Approach below 1,000ft IMC"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${titleError ? '#dc2626' : '#cbd5e1'}`,
                    outline: titleError ? '2px solid rgba(220, 38, 38, 0.25)' : undefined,
                    boxSizing: 'border-box',
                    fontSize: '13px',
                  }}
                />
                {titleError && (
                  <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
                    ⚠️ Tehlike başlığı zorunludur.
                  </div>
                )}
              </div>

              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Taxonomy Category <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${categoryError ? '#dc2626' : '#cbd5e1'}`,
                    outline: categoryError ? '2px solid rgba(220, 38, 38, 0.25)' : undefined,
                    boxSizing: 'border-box',
                    fontSize: '13px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">-- Lütfen Taksonomi Seçiniz / Select Category --</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      [{c.code}] {c.name}
                    </option>
                  ))}
                </select>
                {categoryError && (
                  <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
                    ⚠️ Lütfen bir taksonomi kategorisi seçiniz.
                  </div>
                )}
              </div>
            </div>

            {/* Condition Statement */}
            <div className="form-field" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                1. Unsafe Condition (Existing Physical / Environmental / Procedural State) <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                rows={2}
                placeholder="Describe the existing threat or condition (e.g. Unsecured cargo pallet plastic wrapping blown by jet blast across taxiway Echo)..."
                value={conditionText}
                onChange={(e) => setConditionText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${conditionError ? '#dc2626' : '#cbd5e1'}`,
                  outline: conditionError ? '2px solid rgba(220, 38, 38, 0.25)' : undefined,
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                }}
              />
              {conditionError && (
                <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
                  ⚠️ Tehlikeli durum açıklaması zorunludur.
                </div>
              )}
            </div>

            {/* Consequence Statement */}
            <div className="form-field" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                2. Potential Immediate Consequence (Downstream Chain of Events) <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                rows={2}
                placeholder="Describe what occurs if unchecked (e.g. Plastic sheet ingested into #1 turbofan engine inlet during taxi)..."
                value={consequenceText}
                onChange={(e) => setConsequenceText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${consequenceError ? '#dc2626' : '#cbd5e1'}`,
                  outline: consequenceError ? '2px solid rgba(220, 38, 38, 0.25)' : undefined,
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                }}
              />
              {consequenceError && (
                <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
                  ⚠️ Olası doğrudan sonuç açıklaması zorunludur.
                </div>
              )}
            </div>

            {/* Potential Outcome Statement */}
            <div className="form-field" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                3. Worst Credible Outcome (Accident / Excursion / Inflight Shutdown)
              </label>
              <input
                type="text"
                placeholder="e.g. Compressor stall, air turnback, or uncontained fan blade separation"
                value={potentialOutcome}
                onChange={(e) => setPotentialOutcome(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  boxSizing: 'border-box',
                  fontSize: '13px',
                }}
              />
            </div>

            {/* Severity, Likelihood & Initial Risk Index */}
            <div className="form-grid-3" style={{ gap: '16px', marginBottom: '16px' }}>
              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Severity Estimate
                </label>
                <select
                  value={severityEstimate}
                  onChange={(e) => handleSeverityChange(e.target.value as HazardSeverity)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#fff' }}
                >
                  <option value="CRITICAL">5 - CRITICAL / Catastrophic</option>
                  <option value="HIGH">4 - HIGH / Hazardous</option>
                  <option value="MEDIUM">3 - MEDIUM / Major</option>
                  <option value="LOW">2 - LOW / Minor</option>
                </select>
              </div>

              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Likelihood Estimate
                </label>
                <select
                  value={likelihoodEstimate}
                  onChange={(e) => handleLikelihoodChange(e.target.value as HazardLikelihood)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#fff' }}
                >
                  <option value="FREQUENT">A - FREQUENT</option>
                  <option value="PROBABLE">B - PROBABLE</option>
                  <option value="POSSIBLE">C - POSSIBLE</option>
                  <option value="UNLIKELY">D - UNLIKELY</option>
                  <option value="RARE">E - RARE</option>
                </select>
              </div>

              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Risk Index
                </label>
                <input
                  type="text"
                  readOnly
                  value={initialRiskIndex}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    boxSizing: 'border-box',
                    background: '#f1f5f9',
                    fontWeight: 800,
                    color: '#4338ca',
                    textAlign: 'center',
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>

            {/* Department, Location & Review Cycle */}
            <div className="form-grid-3" style={{ gap: '16px' }}>
              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Responsible Department
                </label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#fff' }}
                >
                  <option value="">-- Departman Seçiniz --</option>
                  {departmentsList.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name} ({d.department_code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Aerodrome / Location
                </label>
                <input
                  type="text"
                  placeholder="e.g. LTFM / Main Apron"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-field">
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  Review Frequency
                </label>
                <select
                  value={reviewFrequencyDays}
                  onChange={(e) => setReviewFrequencyDays(parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#fff' }}
                >
                  <option value={30}>30 Days</option>
                  <option value={60}>60 Days</option>
                  <option value={90}>90 Days</option>
                  <option value={180}>180 Days</option>
                  <option value={365}>365 Days</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action buttons (pinned at bottom, always fully visible) */}
          <div
            className="modal-footer"
            style={{
              flexShrink: 0,
              backgroundColor: '#ffffff',
              borderTop: '1px solid #e2e8f0',
              padding: '14px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              zIndex: 10,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              style={{ width: 'auto', padding: '9px 18px', fontSize: '13px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto', padding: '9px 24px', fontSize: '13px', fontWeight: 700 }}
            >
              {loading ? 'Registering...' : 'Register Safety Hazard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
